import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import './table.css';
import { RiFilterFill } from "react-icons/ri";
import { FaEyeSlash, FaArrowDown, FaArrowUp, FaChevronLeft, FaChevronRight, FaPlus } from 'react-icons/fa';
import { HiViewColumns } from "react-icons/hi2";
import { MdDownload } from "react-icons/md";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from 'jspdf';

const TradeTable = forwardRef(({ headers, rows, edit, sorting, drag, filtering, exporting, addRow, restrictEditToNewRows, plantTypeOptions, fuelTypeOptions, plantUnitOptions }, ref) => {
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [userRows, setUserRows] = useState(rows);
    const [visibleColumns, setVisibleColumns] = useState(
        headers.reduce((acc, header) => ({ ...acc, [header.name]: true }), { checkboxColumn: true })
    );
    const [jumpPage, setJumpPage] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const columDropdownRef = useRef(null);
    const [showSearch, setShowSearch] = useState({});
    const [filters, setFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const [highlightedCells, setHighlightedCells] = useState([]);
    const [draggingCells, setDraggingCells] = useState(null);
    const [selectedCells, setSelectedCells] = useState([]);
    const [tempVisibleColumns, setTempVisibleColumns] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [openDatePicker, setOpenDatePicker] = useState(null);
    const [draggedCells, setDraggedCells] = useState([]);
    const tableRef = useRef(null);
    const [localHeaders, setLocalHeaders] = useState([]);

    useImperativeHandle(ref, () => ({
        getUpdatedData: () => userRows,
    }));

    useEffect(() => {
        if (Array.isArray(headers) && headers.length >= 2 && Array.isArray(headers[0]) && Array.isArray(headers[1])) {
            const allHeaders = [...headers[0], ...headers[1]];
            const visibilityMap = allHeaders.reduce((acc, header) => {
                acc[header.name] = true;
                return acc;
            }, { checkboxColumn: true });

            setVisibleColumns(visibilityMap);
        } else {
          //  console.error("Headers is not an array of arrays", headers);
        }
    }, [headers]);

    useEffect(() => {
        if (headers.length > 1 && Array.isArray(headers[1])) {
            setLocalHeaders(
                headers[1].map((header, index) => ({
                    ...header,
                    originalColumnIndex: index,
                }))
            );
        }
    }, [headers]);
    
    useEffect(() => {
        setUserRows([]);
        setTimeout(() => {
            if (headers.length > 0 && rows.length > 0) {
                // console.log(headers);
                // console.log(rows);
                setUserRows(rows.map((row, index) => ({
                    ...row,
                    originalRowIndex: index,
                })));
            }
        }, 0);
    }, [headers, rows]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setVisibleColumns(tempVisibleColumns);
                setColumnDropdownOpen(false);
            }
        };

        if (columnDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [columnDropdownOpen, setVisibleColumns, tempVisibleColumns]);

    useEffect(() => {
        const container = document.querySelector(".table-container");
        const table = container?.querySelector("table");
        // if (container && table) {
        //     table.style.minWidth = "1500px";
        // }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columDropdownRef.current && !columDropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    const isValidDate = (dateString) => {
        const date = Date.parse(dateString);
        return !isNaN(date);
    };

    const applySorting = (rows) => {
        if (sortConfig.key) {
            const sortedRows = [...rows];
            const direction = sortConfig.direction;
            sortedRows.sort((a, b) => {
                const valueA = a[sortConfig.key];
                const valueB = b[sortConfig.key];
                if (valueA < valueB) return direction === "asc" ? -1 : 1;
                if (valueA > valueB) return direction === "asc" ? 1 : -1;
                return 0;
            });
            return sortedRows;
        }
        return rows;
    };

    const exportCSV = () => {
        if (userRows.length === 0) return;

        const headers = Object.keys(userRows[0]).filter(key => key !== 'originalRowIndex');

        const csvContent =
            [headers.join(",")]
                .concat(userRows.map(row =>
                    headers.map(fieldName => row[fieldName]).join(",")
                ))
                .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "exported_data.csv");
    };

    const handleFileSave = async (blob, fileName) => {
        try {
            if ("showSaveFilePicker" in window) {
                const handle = await window.showSaveFilePicker({
                    suggestedName: fileName,
                    types: [{
                        description: 'CSV Files',
                        accept: { 'text/csv': ['.csv'] }
                    }]
                });
                const writable = await handle.createWritable();
                await writable.write(blob);
                await writable.close();
            } else {
                saveAs(blob, fileName);
            }
        } catch (error) {
            if (error.name === "AbortError") {
             //   console.error("User canceled the file save operation.");
            } else {
             //   console.error("File save error:", error);
            }
        }
    };

    const exportExcel = () => {
        if (userRows.length === 0) return;

        const filteredRows = userRows.map(({ originalRowIndex, ...rest }) => rest);

        const worksheet = XLSX.utils.json_to_sheet(filteredRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

        saveAs(blob, "exported_data.xlsx");
    };

    const exportPDF = () => {
        if (userRows.length === 0) return;

        const doc = new jsPDF();
        const headers = Object.keys(userRows[0]).filter(key => key !== 'originalRowIndex');
        const data = userRows.map(row =>
            headers.map(header => row[header] || '')
        );
        doc.autoTable({
            head: [headers],
            body: data,
        });

        doc.save('exported_data.pdf');
    };

    const handleDelete = () => {
        setUserRows((prevRows) => {
            const updatedRows = prevRows.filter((row) => !selectedRows.includes(row.originalRowIndex));
            const newSelectedRows = selectedRows
                .map(index => updatedRows.findIndex(row => row.originalRowIndex === index))
                .filter(index => index !== -1);

            setSelectedRows(newSelectedRows);
            setSelectAll(false);

            return updatedRows;
        });
    };

    const handleAddRow = () => {
        const newRow = {};

        headers.forEach((header) => {
            const fieldName = typeof header === "string" ? header : header.name;
            const dataType = header.dataType || "string";

            switch (dataType) {
                case "boolean":
                    newRow[fieldName] = false;
                    break;
                case "number":
                    newRow[fieldName] = "";
                    break;
                default:
                    newRow[fieldName] = "";
                    break;
            }
        });

        newRow._isNew = true;

        setUserRows((prevRows) => {
            const updatedRows = [...prevRows, newRow];
            const newTotalPages = Math.ceil(updatedRows.length / rowsPerPage);
            setCurrentPage(newTotalPages);
            return updatedRows;
        });
    };

    const handleExportClick = (option) => {
        if (option === "CSV") exportCSV();
        if (option === "Excel") exportExcel();
        if (option === "Print") exportPDF();
        setIsDropdownOpen(false);
    };


    const applyFilters = (rows) => {
        let filteredRows = [...rows];
        Object.keys(filters).forEach((key) => {
            const value = filters[key];
            if (value) {
                filteredRows = filteredRows.filter((row) =>
                    row[key]?.toString().toLowerCase().includes(value.toLowerCase())
                );
            }
        });
        return filteredRows;
    };

    const handleOpenDropdown = () => {
        setTempVisibleColumns(visibleColumns);
        setColumnDropdownOpen(true);
    };

    const handleCheckboxChange = (header) => {
        setTempVisibleColumns((prev) => {
            const updatedColumns = {
                ...prev,
                [header]: !prev[header],
            };

            const isAnyDataColumnVisible = Object.entries(updatedColumns)
                .filter(([key]) => key !== "checkboxColumn")
                .some(([, value]) => value);

            return {
                ...updatedColumns,
                checkboxColumn: isAnyDataColumnVisible,
            };
        });
    };

    const setAllColumnsVisibility = (isVisible) => {
        const updatedColumns = Object.fromEntries(
            headers[1].map((header) => [header.name, isVisible])
        );
        updatedColumns.checkboxColumn = isVisible; 
        setTempVisibleColumns(updatedColumns);
    };
    
    const isAnyColumnVisible = Object.values(visibleColumns).some(value => value);
    const rowsToDisplay = applySorting(applyFilters(userRows));
    const totalPages = rowsToDisplay.length;
    const startIndex = Math.min((currentPage - 1) * rowsPerPage, userRows.length);
    const endIndex = Math.min(startIndex + rowsPerPage, userRows.length);
    const currentRows = rowsToDisplay.slice(startIndex, endIndex);

    const handleJumpToPage = () => {
        const pageNumber = parseInt(jumpPage, 10);
        if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
        setJumpPage("");
    };

    // const handleCellEdit = (rowIndex, header, value) => {
    //     if (!header.editable) return;
    //     const updatedRows = [...userRows];
    //     updatedRows[rowIndex][header.name] = value;
    //     setUserRows(updatedRows);
    // };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        if (isNaN(date)) return dateStr;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}/${month}/${day}`;
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            setDraggingCells(null);
            setHighlightedCells([]);
        }
    };

    const handleCellBlur = (e, header, rowIndex) => {
        if (header.editable) {
            const columnType = header.dataType;
            if (columnType === 'date') {
                return;
            }
            if (columnType === 'boolean') {
                return;
            }
            if (columnType === 'text' && (header === 'planttype' || header === 'fueltype')) {
                return;
            }
            handleCellEdit(rowIndex, header, e.target.innerText);
        }
    };

    const handleDragStart = (value, rowIndex, columnIndex, e) => {
        if (drag === false) return;
        const header = headers[1][columnIndex];
        if (!header.editable) return;

        const actualRowIndex = (currentPage - 1) * rowsPerPage + rowIndex;
        setDraggingCells({ startRow: actualRowIndex, startColumn: columnIndex, value });
        setHighlightedCells([{ row: actualRowIndex, column: columnIndex }]);

        e.dataTransfer.setData("text/plain", value);
        e.dataTransfer.effectAllowed = "move";

        const dragImage = new Image();
        e.dataTransfer.setDragImage(dragImage, 0, 0);
    };

    const handleDragOver = (rowIndex, columnIndex, e) => {
        e.preventDefault();

        const header = headers[1][columnIndex]; 
        if (!header.editable || !draggingCells) return;

        const { startRow, startColumn } = draggingCells;
        let cellsToHighlight = [];

        if (startRow === rowIndex) {
            const start = Math.min(startColumn, columnIndex);
            const end = Math.max(startColumn, columnIndex);
            cellsToHighlight = Array.from({ length: end - start + 1 }, (_, i) => ({
                row: rowIndex,
                column: start + i,
            }));
        } else if (startColumn === columnIndex) {
            const start = Math.min(startRow, rowIndex);
            const end = Math.max(startRow, rowIndex);
            cellsToHighlight = Array.from({ length: end - start + 1 }, (_, i) => ({
                row: start + i,
                column: columnIndex,
            }));
        }

        setHighlightedCells(cellsToHighlight);
    };

    const restrictInput = (e, header) => {
        const columnType = header.dataType;
        const inputElement = e.target;
        const inputText = inputElement.innerText;
        let cleanedText = inputText;

        if (columnType === 'number') {
            cleanedText = inputText.replace(/[^0-9.]/g, '');
            const parts = cleanedText.split('.');
            if (parts.length > 2) {
                cleanedText = parts[0] + '.' + parts.slice(1).join('').replace(/\./g, '');
            }
        } else if (columnType === 'string') {
            cleanedText = inputText.replace(/[^A-Za-z\s]/g, '');
        }

        if (cleanedText !== inputText) {
            inputElement.innerText = cleanedText;
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(inputElement);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    const handleDrop = (rowIndex, columnIndex, e) => {
        if (!draggingCells) return;

        const { startRow, startColumn, value } = draggingCells;
        let updatedRows = [...userRows];
        let newHighlightedCells = [];

        const header = headers[1][columnIndex]; 
        if (!header.editable) return;

        if (startRow === rowIndex) {
            const start = Math.min(startColumn, columnIndex);
            const end = Math.max(startColumn, columnIndex);
            for (let col = start; col <= end; col++) {
                updatedRows[rowIndex][headers[col].name] = value;
                newHighlightedCells.push({ row: rowIndex, column: col });
            }
        } else if (startColumn === columnIndex) {
            const start = Math.min(startRow, rowIndex);
            const end = Math.max(startRow, rowIndex);
            for (let row = start; row <= end; row++) {
                updatedRows[row][headers[1][columnIndex].name] = value;
                newHighlightedCells.push({ row, column: columnIndex });
            }
        }

        setUserRows(updatedRows);
        setDraggingCells(null);
        setHighlightedCells(newHighlightedCells);
        setDraggedCells(newHighlightedCells);
    };

    const handleCellClick = (rowIndex, columnIndex, header) => {
        if (header.editable) {
            setSelectedCells([{ row: rowIndex, column: columnIndex }]);
            setHighlightedCells([{ row: rowIndex, column: columnIndex }]);
            setDraggedCells([]);
        }
    };

    const handleCellDoubleClick = (rowIndex, columnIndex) => {
        setSelectedCells([{ row: rowIndex, column: columnIndex }]);
    };

    const handleCellEdit = (rowIndex, header, value) => {
        if (!header.editable) return;

        const isDate = header.dataType === "date";

        const rawValue =
            typeof value === 'object' && value !== null
                ? value?.fuel_type || value?.plant_type || ''
                : value;

        const formattedValue = isDate
            ? formatDate(rawValue)
            : header.dataType === "boolean"
                ? rawValue === true || rawValue === "true"
                : header.dataType === "number" && !isNaN(rawValue)
                    ? Number(rawValue)
                    : rawValue;

        setUserRows((prevRows) =>
            prevRows.map((row, index) => {
                if (index !== rowIndex) return row;
                if (restrictEditToNewRows && !row._isNew) return row;
                return { ...row, [header.name]: formattedValue };
            })
        );

    };

    const renderCell = (row, rowIndex, header, value, columnIndex) => {
        const actualRowIndex = row.originalRowIndex;
        const isSelected = selectedCells.some(
            (cell) => cell.row === actualRowIndex && cell.column === columnIndex
        );
        const isHighlighted = highlightedCells.some(
            (cell) => cell.row === actualRowIndex && cell.column === columnIndex
        );
        const isDragged = draggingCells &&
            highlightedCells.some(cell => cell.row === actualRowIndex && cell.column === columnIndex);
        const isPreviouslyDragged = draggedCells.some(
            (cell) => cell.row === actualRowIndex && cell.column === columnIndex
        );

        const columnType = header.dataType;
        const normalizedBooleanValue =
            typeof value === 'boolean' ? value : String(value).toLowerCase() === 'true';

        const isSticky = !header.editable;

        if (['fuel_type', 'plant_type', 'plant_name', 'unit_name'].includes(header.name)) {
            if (isSelected && header.editable) {
                let options = [];

                if (header.name === 'fuel_type') {
                    options = fuelTypeOptions.map(opt => opt.fuel_type);
                } else if (header.name === 'plant_type') {
                    options = plantTypeOptions.map(opt => opt.plant_type);
                } else if (header.name === 'plant_name') {
                    options = [...new Set(plantUnitOptions.map(opt => opt.plant_name))];
                } else if (header.name === 'unit_name') {
                    const plantName = row['plant_name'];
                    options = plantUnitOptions
                        .filter(opt => opt.plant_name === plantName)
                        .map(opt => opt.unit_name);
                }

                return (
                    <td
                        key={header.name}
                        className={`th-td editable-cell draggable-cell 
                            ${isHighlighted ? 'highlighted-cell' : ''} 
                            ${isDragged ? 'dragging-cell' : ''} 
                            ${isSticky ? 'sticky-column' : ''} 
                            ${isPreviouslyDragged ? 'dragged-cell' : ''} 
                            ${columnIndex === 0 && !dropdownOpen[header.name] ? 'sticky-column' : ''}`}
                        style={{
                            position: isSticky ? "sticky" : "relative",
                            left: isSticky ? `${columnIndex * 120}px` : "auto",
                            zIndex: isSticky ? 2 : 1,
                        }}
                        onDragStart={(e) => handleDragStart(value, rowIndex, columnIndex, e)}
                        onDragOver={(e) => handleDragOver(actualRowIndex, columnIndex, e, header)}
                        onDrop={(e) => handleDrop(actualRowIndex, columnIndex, e)}
                        onClick={() => handleCellClick(actualRowIndex, columnIndex, header)}
                        onDoubleClick={() => handleCellDoubleClick(actualRowIndex, columnIndex)}
                        draggable={header.editable}
                    >
                        <select
                            value={value || ''}
                            onChange={(e) => handleCellEdit(actualRowIndex, header, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <option value="">Select {header.displayName || header.name}</option>
                            {options.map((opt, i) => (
                                <option key={i} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </td>
                );
            } else {
                return (
                    <td
                        key={header.name}
                        className={`th-td draggable-cell 
                            ${isHighlighted ? 'highlighted-cell' : ''} 
                            ${isDragged ? 'dragging-cell' : ''} 
                            ${isSticky ? 'sticky-column' : ''} 
                            ${isPreviouslyDragged ? 'dragged-cell' : ''} 
                            ${columnIndex === 0 && !dropdownOpen[header.name] ? 'sticky-column' : ''}`}
                        style={{
                            position: isSticky ? "sticky" : "relative",
                            left: isSticky ? `${columnIndex * 120}px` : "auto",
                            zIndex: isSticky ? 2 : 1,
                        }}
                        onClick={() => handleCellClick(actualRowIndex, columnIndex, header)}
                        onDoubleClick={() => handleCellDoubleClick(actualRowIndex, columnIndex)}
                        draggable={header.editable}
                    >
                        {value ?? ''}
                    </td>
                );
            }
        }

        return (
            <td
                key={header.name}
                className={`th-td editable-cell draggable-cell 
                    ${isHighlighted ? 'highlighted-cell' : ''} 
                    ${isDragged ? 'dragging-cell' : ''} 
                    ${isSticky ? 'sticky-column' : ''} 
                    ${isPreviouslyDragged ? 'dragged-cell' : ''} 
                    ${columnIndex === 0 && !dropdownOpen[header.name] ? 'sticky-column' : ''}`}
                style={{
                    position: isSticky ? "sticky" : "relative",
                    left: isSticky ? `${columnIndex * 120}px` : "auto",
                    zIndex: isSticky ? 2 : 1,
                }}
                contentEditable={
                    header.editable &&
                    isSelected &&
                    columnType !== 'boolean' &&
                    columnType !== 'date' &&
                    header.name !== 'plant_type' &&
                    header.name !== 'fuel_type' &&
                    (!restrictEditToNewRows || row._isNew)
                }
                suppressContentEditableWarning={true}
                onBlur={(e) => handleCellBlur(e, header, actualRowIndex)}
                onInput={(e) => restrictInput(e, header)}
                onDragStart={(e) => handleDragStart(value, rowIndex, columnIndex, e)}
                onDragOver={(e) => handleDragOver(actualRowIndex, columnIndex, e, header)}
                onDrop={(e) => handleDrop(actualRowIndex, columnIndex, e)}
                onClick={() => handleCellClick(actualRowIndex, columnIndex, header)}
                onDoubleClick={() => handleCellDoubleClick(actualRowIndex, columnIndex)}
                draggable={header.editable}
            >
                {header.editable && isSelected && columnType === 'date' ? (
                    <input
                        type="date"
                        value={typeof value === 'string' ? value.replace(/\//g, '-') : ''}
                        onChange={(e) => handleCellEdit(actualRowIndex, header, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : header.editable && isSelected && columnType === 'boolean' ? (
                    <select
                        value={normalizedBooleanValue ? 'true' : 'false'}
                        onChange={(e) => handleCellEdit(actualRowIndex, header, e.target.value === 'true')}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <option value="true">TRUE</option>
                        <option value="false">FALSE</option>
                    </select>
                ) : columnType === 'boolean' ? (
                    normalizedBooleanValue ? 'TRUE' : 'FALSE'
                ) : (
                    typeof value === 'object' ? '' : value ?? ''
                )}
            </td>
        );
    };

    const handleSort = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ key, direction });
        const sortedRows = [...userRows];
        const sortedData = sortedRows.sort((a, b) => {
            const valueA = a[key];
            const valueB = b[key];
            if (valueA < valueB) return direction === "asc" ? -1 : 1;
            if (valueA > valueB) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setUserRows(sortedData);
    };

    const handleRowSelect = (rowIndex) => {
        setSelectedRows((prev) =>
            prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex]
        );
    };

    const toggleSearchBar = (key) => {
        setShowSearch((prevState) => ({
            ...prevState,
            [key]: !prevState[key],
        }));
    };

    const toggleDropdown = (key) => {
        setDropdownOpen((prevState) => {
            const isOpen = !prevState[key];

            if (isOpen) {
                document.querySelector(`.sticky-column`)?.classList.remove('sticky-column');
            } else {
                document.querySelector(`td[data-key="${key}"]`)?.classList.add('sticky-column');
            }

            return {
                ...prevState,
                [key]: isOpen,
            };
        });
    };

    const handleFilterChange = (key, value) => {
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters, [key]: value };
            const filteredRows = applyFilters(userRows);
            return newFilters;
        });
    };

    const HideColumn = (header) => {
        setVisibleColumns((prev) => ({
            ...prev,
            [header]: false,
        }));
    };

    const handleDropdownAction = (header, action) => {
        action();
        setDropdownOpen(prev => ({ ...prev, [header]: false }));
    };

    const handleDateColumnChange = (header, value) => {
        const updatedRows = userRows.map(row => ({
            ...row,
            [header]: value
        }));
        setUserRows(updatedRows);
        setOpenDatePicker(null);
    };

    const dropdownActions = (header) => [
        {
            label: "Sort by Asc",
            icon: <FaArrowUp />,
            action: () => handleSort(header.name, "asc")
        },
        {
            label: "Sort by Desc",
            icon: <FaArrowDown />,
            action: () => handleSort(header.name, "desc")
        },
        {
            label: "Filter",
            icon: <RiFilterFill />,
            action: () => toggleSearchBar(header.name)
        },
        {
            label: "Hide column",
            icon: <FaEyeSlash />,
            action: () => HideColumn(header.name)
        },
        {
            label: "Manage column",
            icon: <HiViewColumns />,
            action: () => setColumnDropdownOpen(!columnDropdownOpen)
        }
    ];

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedRows([]);
        } else {
            setSelectedRows(userRows.map((_, index) => index));
        }
        setSelectAll(!selectAll);
    };

    const toggleDatePicker = (header) => {
        setOpenDatePicker(openDatePicker === header ? null : header);
    };

    const handleRowsPerPageChange = (event) => {
        let value = event.target.value;
        if (value === "") {
            setRowsPerPage("");
            return;
        }
        value = parseInt(value, 10);
        if (!isNaN(value)) {
            if (value < 1) {
                value = 10;
            }
            setRowsPerPage(Math.min(value, userRows.length));
            setCurrentPage(1);
        }
    };

    return (
        <div className="table-container">
            <div >
                <div className="table-top-bar">
                    {/* Column Icon */}
                    {filtering && (
                        <div ref={dropdownRef} className="table-filtering">
                            <HiViewColumns
                                className="table-filtering-icon"
                                onClick={() => setColumnDropdownOpen(!columnDropdownOpen)}
                            />
                            <span className="table-filtering-name" onClick={handleOpenDropdown}>
                                COLUMNS
                            </span>

                            {columnDropdownOpen && headers.length > 1 && (
                                <div className="table-filtering-dropdown">
                                    {/* Search Bar */}
                                    <input
                                        type="text"
                                        placeholder="Find Column"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="table-column-inputBox"
                                    />

                                    {/* Filtered Column List from second header */}
                                    <div className="table-filtering-columnList">
                                        {headers[1]
                                            .filter(header => header.name && header.name.toLowerCase().includes(searchTerm.toLowerCase()))
                                            .map(header => (
                                                <div key={header.name} className="table-filtering-columnList-name">
                                                    <label className="toggle-switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={tempVisibleColumns[header.name] || false}
                                                            onChange={() => handleCheckboxChange(header.name)}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                    {header.name}
                                                </div>
                                            ))}
                                    </div>

                                    {/* Hide All / Show All Controls */}
                                    <div className="table-filtering-columnList-all">
                                        <span className="table-filtering-columnList-alls" onClick={() => setAllColumnsVisibility(false)}>
                                            HIDE ALL
                                        </span>
                                        <span className="table-filtering-columnList-alls" onClick={() => setAllColumnsVisibility(true)}>
                                            SHOW ALL
                                        </span>
                                    </div>
                                </div>
                            )}

                        </div>
                    )}

                    {/* Export Icon */}
                    {exporting && (
                        <div className="table-export">
                            <MdDownload
                                className="table-export-icon"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            />
                            <span className="table-filtering-columnList-export" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>EXPORT</span>

                            {/* Dropdown Menu */}
                            {isDropdownOpen && (
                                <div ref={columDropdownRef}
                                    className="table-export-dropDown"
                                >
                                    {[
                                        { label: "Download as CSV", action: "CSV" },
                                        { label: "Download as Excel", action: "Excel" },
                                        { label: "Download as PDF", action: "Print" }
                                    ].map((option) => (
                                        <div
                                            key={option.action}
                                            className="table-export-dropDown-name"
                                            onClick={() => handleExportClick(option.action)}
                                        >
                                            {option.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {addRow && (
                        <div className="table-addRow">
                            <FaPlus
                                onClick={handleAddRow}
                                className="table-addRow"
                            />
                            <span className="table-filtering-columnList-export" onClick={handleAddRow}>Add Row</span>
                        </div>
                    )}
                </div>
                {selectedRows.length > 0 && (
                    <div className="table-delete">
                        <span>{selectedRows.length} selected  </span>
                        {selectedRows.length === 1 ? (
                            <button onClick={handleDelete} className="table-delete-button">
                                Delete
                            </button>
                        ) : (
                            <button onClick={handleDelete} className="table-delete-button">
                                Delete All
                            </button>
                        )}
                    </div>
                )}


                <div ref={tableRef} className="table-main" style={{ maxHeight: "600px", overflowY: "auto", overflowX: "auto" }}>

                    <table border="1" cellPadding="10" className="table-clean">
                        <thead>
                            {Array.isArray(headers) && headers.length >= 2 && Array.isArray(headers[0]) && Array.isArray(headers[1]) && (
                                <>
                                    <tr>
                                        {edit && visibleColumns.checkboxColumn && (
                                            <th key="checkbox-header" rowSpan={2}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectAll}
                                                    onChange={handleSelectAll}
                                                    className="table-checkBox"
                                                />
                                            </th>
                                        )}
                                        {headers[0]
                                            .filter(header => visibleColumns[header.name])
                                            .map((header, index) => (
                                                <th
                                                    key={`top-header-${index}`}
                                                    className="sticky-header"
                                                >
                                                    <div className="table-th-name">
                                                        <span className="table-th-displayName">{header.displayName}</span>
                                                    </div>
                                                </th>
                                            ))}
                                    </tr>
                                    <tr>
                                        {headers[1]
                                            .filter(header => visibleColumns[header.name])
                                            .map((header, index) => (
                                                <th
                                                    key={`second-header-${index}`}
                                                    className="sticky-header"
                                                >
                                                    <div className="table-th-name">
                                                        <span className="table-th-displayName">{header.displayName}</span>
                                                    </div>
                                                </th>
                                            ))}
                                    </tr>
                                </>
                            )}
                        </thead>
                        <tbody>
                            {Array.isArray(currentRows) &&
                                currentRows.map((row, rowIndex) => (
                                    <tr key={`row-${row.originalRowIndex ?? rowIndex}`}>
                                        {edit && visibleColumns.checkboxColumn && (
                                            <td key={`checkbox-${row.originalRowIndex ?? rowIndex}`}>
                                                <input
                                                    type="checkbox"
                                                    className="table-checkBox"
                                                    checked={selectedRows.includes(rowIndex)}
                                                    onChange={() => handleRowSelect(rowIndex)}
                                                />
                                            </td>
                                        )}

                                        {Array.isArray(headers[1]) &&
                                            localHeaders
                                                .filter(header => visibleColumns[header.name])
                                                .map((header) => {
                                                    const cellValue = row[header.name] !== undefined ? row[header.name] : "-";
                                                    return renderCell(
                                                        row,
                                                        rowIndex,
                                                        header,
                                                        cellValue,
                                                        header.originalColumnIndex
                                                    );
                                                })}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <br></br>
            <div>
                {isAnyColumnVisible && (
                    <div className="table-pagination">
                        <label>Rows per page: </label>
                        <input
                            type="text"
                            value={rowsPerPage}
                            onChange={handleRowsPerPageChange}
                            style={{ width: "50px", textAlign: "center" }}
                        />
                        <span className="table-pagination">
                            {startIndex}–{endIndex} of {totalPages}
                        </span>

                        <span
                            onClick={() => currentPage > 1 && goToPage(currentPage - 1)}
                            style={{
                                cursor: currentPage === 1 ? 'default' : 'pointer',
                            }}
                        >
                            <FaChevronLeft />
                        </span>
                        <span
                            onClick={() => endIndex < totalPages && goToPage(currentPage + 1)}
                            style={{
                                cursor: endIndex === totalPages ? 'default' : 'pointer',
                            }}
                        >
                            <FaChevronRight />
                        </span>
                    </div>
                )}
            </div>
            <br></br>
        </div>
    );
});

export default TradeTable;